import React from 'react'
import styles from './Step3.scss'
import { S2DLayout } from '../../../S2DLayout'
import { s2dMessages } from '../../../intlMessages'
import { FancyCheckmark } from 'common/FancyCheckmark'
import { CircleSpinner } from 'common/spinners'
import { Icon } from 'common/Icon'
import base from 'lib/styleguide/base.scss'
import { Msg, fm, useFormatMessage, commonNouns } from 'lib/localization'
import cx from 'classnames'
import { FormattedMessage as T, injectIntl, defineMessages } from 'react-intl'

import iosFirstInstallMp4 from 'assets/images/send_to_device/ios_first_install.mp4'
import iosFirstInstallWebm from 'assets/images/send_to_device/ios_first_install.webm'

import androidFirstInstallMp4 from 'assets/images/send_to_device/android_first_install.mp4'
import androidFirstInstallWebm from 'assets/images/send_to_device/android_first_install.webm'

import androidDlPromptMp4 from 'assets/images/send_to_device/android_dl_prompt.mp4'
import androidDlPromptWebm from 'assets/images/send_to_device/android_dl_prompt.webm'

import iphonePushNotificationMp4 from 'assets/images/send_to_device/ios_push_notification.mp4'
import iphonePushNotificationWebm from 'assets/images/send_to_device/ios_push_notification.webm'

const message = defineMessages({
  downloadTip: {
    id: 'Step3.downloadTip',
    defaultMessage: 'Download allows you to view maps and navigate routes when your phone does not have an internet connection'
  }
})

class _Step3 extends React.PureComponent {
  constructor (props) {
    super(props)
    // don't animate the checkmark in if the push was sent on mount
    this.state = {
      noAnimate: props.pushSent
    }
  }

  componentDidMount () {
    // start a push if the push hasn't been sent yet
    const { pushSent, pushPending, onSendPush } = this.props
    if (!(pushSent || pushPending)) {
      onSendPush()
    }
  }

  onSendPush = () => {
    this.setState({ noAnimate: false })
    this.props.onSendPush()
  }

  render () {
    const {
      title,
      subtitle,
      step,
      onNext,
      onPrev,
      pushSent,
      pushPending,
      pushError,
      appInstalled,
      selectedDevice,
      stepCount,
      intl
    } = this.props

    const { noAnimate } = this.state

    const StepToDisplay = appInstalled ? Step3b : Step3a
    return (
      <S2DLayout
        title={title}
        subtitle={subtitle}
        step={step}
        onClickNext={onNext}
        onClickPrev={onPrev}
        stepCount={stepCount}>
        <StepToDisplay selectedDevice={selectedDevice}>
          {pushPending && <div className={styles.status}>
            <CircleSpinner size={40} className={styles.spinner} />
            <h3><Msg m={s2dMessages.sending} values={{ asset: fm(intl, commonNouns.routes, { count: 1 }) }} />Sending route</h3>
          </div>}
          {pushSent && <div className={styles.status}>
            <FancyCheckmark noAnimate={noAnimate} />
            <h3><T id='s2d.mobleStep31' defaultMessage='Route sent!' /></h3>
            <div><a onClick={this.onSendPush}><T id='s2d.mobleStep32' defaultMessage='Click here to re-send' /></a></div>
          </div>}
          {pushError && <div className={cx(styles.status, base.errorMessage)}>
            <h3><T id='s2d.pushError' defaultMessage='Push error' /></h3>
            <div>
              <T id='s2d.mobleStep33' defaultMessage='Send to Device requires you to be logged into the website and to have the mobile app installed.' />.
            </div>
          </div>}
        </StepToDisplay>
      </S2DLayout>
    )
  }
}

export const Step3 = injectIntl(_Step3)

const Step3a = ({ children, selectedDevice }) => {
  const webm = selectedDevice === 'iphone'
    ? iosFirstInstallWebm
    : androidFirstInstallWebm

  const mp4 = selectedDevice === 'iphone'
    ? iosFirstInstallMp4
    : androidFirstInstallMp4

  return (
    <div className={styles.step3a}>
      <div className={styles.col}>
        <video width='240' height='426' autoPlay muted loop key='step3'>
          <source src={webm} type='video/webm' />
          <source src={mp4} type='video/mp4' />
        </video>
      </div>
      <div className={styles.col}>
        <h3><T id='s2d.mobleStep34' defaultMessage='Open the Ride with GPS app and sign in to your account.' /></h3>
        <h3>
          <T
            id='s2d.mobleStep35'
            defaultMessage="Once you're logged in, go to <strong>Routes</strong> and tap <strong>Pinned</strong> to see your route."
            values={{ strong: (...c) => <strong>{c}</strong> }} />
        </h3>
        {children}
      </div>
    </div>
  )
}

const Step3b = ({ children, selectedDevice }) => {
  const fm = useFormatMessage()
  const offlineHelp = selectedDevice === 'iphone'
    ? 'https://ridewithgps.com/help/offline-maps-iphone'
    : 'https://ridewithgps.com/help/offline-maps-android'

  const webm = selectedDevice === 'iphone'
    ? iphonePushNotificationWebm
    : androidDlPromptWebm

  const mp4 = selectedDevice === 'iphone'
    ? iphonePushNotificationMp4
    : androidDlPromptMp4
  return (
    <div className={styles.step3b}>
      <div className={styles.col}>
        <video width='240' height='426' autoPlay muted loop key='step3'>
          <source src={webm} type='video/webm' />
          <source src={mp4} type='video/mp4' />
        </video>
      </div>
      <div className={styles.col}>
        <h3><T id='s2d.mobleStep3b1' defaultMessage='Tap the notification on your phone to open the route.' /></h3>
        <h3>
          <T
            id='s2d.mobleStep3b2'
            defaultMessage='You will be prompted to View or <strong>Download</strong> <a>{icon}</a> your route.'
            values={{
              strong: (...c) => <strong>{c}</strong>,
              a: (...c) => <a
                data-tip
                title={fm(message.downloadTip)}
                target='_blank'
                href={offlineHelp}
                className={styles.helpIcon}>
                {c}
              </a>,
              icon: <Icon i='help-circled' />
            }} />
        </h3>
        {children}
      </div>
    </div>
  )
}
